import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, Sparkles } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useNotifications, useReadNotification } from "@/hooks/useNotifications";
import { cn } from "@/lib/utils";

const when = (iso) => new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

const Item = ({ n, onOpen }) => (
  <button type="button" onClick={() => onOpen(n)} data-testid={`notification-${n.id}`}
    className={cn("flex w-full gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-accent", !n.read && "bg-brand/5")}>
    <span className={cn("mt-1.5 h-2 w-2 shrink-0 rounded-full", n.read ? "bg-transparent" : "bg-brand")} />
    <span className="min-w-0 flex-1">
      <span className="block text-sm font-medium">{n.title}</span>
      <span className="line-clamp-2 text-xs text-muted-foreground">{n.body}</span>
      <span className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground">
        {n.source?.startsWith("agent") && <><Sparkles className="h-3 w-3 text-brand-gold" />AI Project Manager ·</>} {when(n.created_at)}
      </span>
    </span>
  </button>
);

export const NotificationBell = ({ className, side = "bottom", align = "end" }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { data } = useNotifications();
  const read = useReadNotification();
  const items = data?.items || [];
  const unread = data?.unread || 0;
  const openItem = (n) => {
    if (!n.read) read.mutate(n.id);
    setOpen(false);
    if (n.link) navigate(n.link);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" aria-label="Notifiche" data-testid="notification-bell"
          className={cn("relative inline-flex h-11 w-11 items-center justify-center rounded-xl hover:bg-accent", className)}>
          <Bell className="h-5 w-5" />
          {unread > 0 && <span className="absolute right-1.5 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-brand-gold px-1 text-[10px] font-bold text-[#121014]" data-testid="notification-unread">{unread > 99 ? "99+" : unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent side={side} align={align} collisionPadding={8} className="w-[min(92vw,380px)] rounded-2xl p-2" data-testid="notification-panel">
        <div className="flex items-center justify-between px-3 py-2">
          <p className="font-heading text-sm font-semibold">Notifiche</p>
          {unread > 0 && <button type="button" onClick={() => read.mutate(null)} className="flex items-center gap-1 text-xs text-brand hover:underline" data-testid="notification-read-all"><CheckCheck className="h-3.5 w-3.5" />Segna tutte come lette</button>}
        </div>
        <div className="max-h-[60vh] space-y-0.5 overflow-y-auto">
          {items.map((n) => <Item key={n.id} n={n} onOpen={openItem} />)}
          {items.length === 0 && <p className="px-3 py-8 text-center text-sm text-muted-foreground" data-testid="notification-empty">Nessuna notifica.</p>}
        </div>
      </PopoverContent>
    </Popover>
  );
};
